import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type { ListingKind, WishView } from '@dunea/shared';
import { apiCreateWish, apiListWishes, apiVoteWish } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Segmented,
  Select,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  toggleVote,
  WISH_BODY_MAX,
  WISH_KINDS,
  WISH_SORTS,
  WISH_TITLE_MAX,
  wishDraftError,
  wishStatusMeta,
} from './community-model';
import { apiErrorMessage, kindLabel } from './marketplace-model';

/** What people want built. Vote, or post a wish; creators claim them from the developer portal. */
export function WishlistView({
  onOpenListing,
  focusWishId = null,
}: {
  onOpenListing: (slug: string) => void;
  /** Scroll to (and highlight) this wish once the list loads. */
  focusWishId?: string | null;
}) {
  const [sort, setSort] = useState<'top' | 'new'>('top');
  const wishes = useResource(() => apiListWishes({ sort }), `wishes:${sort}`);
  const [posting, setPosting] = useState(false);
  const loaded = Boolean(wishes.data);

  useEffect(() => {
    if (loaded && focusWishId) {
      document.getElementById(`wish-${focusWishId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [loaded, focusWishId]);

  const replace = (next: WishView) => wishes.setData((prev) => (prev ?? []).map((w) => (w.id === next.id ? next : w)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Segmented value={sort} options={WISH_SORTS} onChange={setSort} label="Sort wishes" />
        {!posting && (
          <button type="button" onClick={() => setPosting(true)} className={BTN_SECONDARY}>
            <div className="i-ph:plus text-[13px]" />
            Post a wish
          </button>
        )}
      </div>

      {posting && (
        <WishForm
          onCancel={() => setPosting(false)}
          onPosted={(w) => {
            wishes.setData((prev) => [w, ...(prev ?? []).filter((x) => x.id !== w.id)]);
            setPosting(false);
          }}
        />
      )}

      {wishes.error && !wishes.loading ? (
        <LoadError message="Could not load the wishlist." onRetry={() => void wishes.reload()} />
      ) : !wishes.data ? (
        <div className="space-y-2.5">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : wishes.data.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-6 text-center text-[12px] text-muted')}>
          No wishes yet. Post the plugin, theme, template or skill you want.
        </div>
      ) : (
        <ul className="space-y-2">
          {wishes.data.map((w) => (
            <WishRow
              key={w.id}
              wish={w}
              focused={w.id === focusWishId}
              onChanged={replace}
              onOpenListing={onOpenListing}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function WishRow({
  wish,
  focused,
  onChanged,
  onOpenListing,
}: {
  wish: WishView;
  focused: boolean;
  onChanged: (next: WishView) => void;
  onOpenListing: (slug: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const status = wishStatusMeta(wish.status);

  const vote = async () => {
    const before = wish;
    onChanged(toggleVote(wish));
    setBusy(true);

    try {
      onChanged(await apiVoteWish(wish.id));
    } catch (err) {
      onChanged(before);
      toast.error(apiErrorMessage(err, 'Could not save your vote.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      id={`wish-${wish.id}`}
      className={classNames(CARD, 'flex items-start gap-3 p-3.5', focused && '!border-accent/40')}
    >
      <button
        type="button"
        onClick={() => void vote()}
        disabled={busy}
        aria-pressed={wish.votedByMe}
        aria-label={wish.votedByMe ? 'Remove your vote' : 'Vote for this'}
        className={classNames(
          'flex w-11 shrink-0 flex-col items-center rounded-lg border py-1.5 transition-colors',
          wish.votedByMe
            ? 'border-accent/40 bg-accent/10 text-accent'
            : 'border-subtle text-secondary hover:border-prominent',
        )}
      >
        <div className={classNames(wish.votedByMe ? 'i-ph:caret-up-fill' : 'i-ph:caret-up', 'text-[13px]')} />
        <span className="text-[12px] font-medium [font-variant-numeric:tabular-nums]">{wish.voteCount}</span>
      </button>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[13px] font-medium text-primary">{wish.title}</span>
          <Pill tone={status.tone} label={status.label} />
        </div>
        {wish.body && <p className="line-clamp-3 text-[11.5px] leading-relaxed text-secondary">{wish.body}</p>}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted">
          <span>{kindLabel(wish.kind)}</span>
          {wish.claimedBy && <span>Claimed by {wish.claimedBy.displayName}</span>}
          {wish.listing && (
            <button
              type="button"
              onClick={() => onOpenListing(wish.listing!.slug)}
              className="text-secondary underline decoration-subtle underline-offset-2 hover:text-primary"
            >
              See {wish.listing.name}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function WishForm({ onPosted, onCancel }: { onPosted: (wish: WishView) => void; onCancel: () => void }) {
  const [kind, setKind] = useState<ListingKind>('plugin');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = wishDraftError({ title, body });

  const submit = async () => {
    setTried(true);

    if (problem) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const wish = await apiCreateWish({ kind, title: title.trim(), ...(body.trim() ? { body: body.trim() } : {}) });
      toast.success('Wish posted.');
      onPosted(wish);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not post your wish.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className={classNames(CARD, 'space-y-3 p-4')}
    >
      <h4 className={CAPTION}>Post a wish</h4>
      <div className="grid grid-cols-[120px_1fr] gap-2">
        <Field label="Kind">
          <Select value={kind} onChange={(v) => setKind(v as ListingKind)} disabled={busy} label="Kind">
            {WISH_KINDS.map(([v, text]) => (
              <option key={v} value={v}>
                {text}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="What should it do?">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={WISH_TITLE_MAX}
            placeholder="Print Shiprocket labels for paid orders"
            disabled={busy}
            className="ui-input !py-2"
          />
        </Field>
      </div>
      <Field label="Details (optional)">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={WISH_BODY_MAX}
          disabled={busy}
          className="ui-input resize-y"
        />
      </Field>
      {tried && problem && <p className="text-[11px] text-danger">{problem}</p>}
      {error && <ErrorBanner message={error} />}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          Post
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
